import * as Yup from 'yup';

const optionalNumber = Yup.number()
    .transform((value, originalValue) => {
        if (originalValue === '' || originalValue === null || originalValue === undefined) {
            return undefined;
        }
        return Number.isNaN(value) ? undefined : value;
    })
    .optional()
    .nullable();

export const settingsFormSchema = Yup.object().shape({
    companyName: Yup.string().required('Please, provide the company name'),
    contactFirstName: Yup.string().required(
        'Please, provide the contact first name'
    ),
    contactLastName: Yup.string().required(
        'Please, provide the contact last name'
    ),
    streetAddress: Yup.string().optional().nullable(),
    stateAddress: Yup.string().optional().nullable(),
    zipcodeAddress: Yup.number().typeError('Zipcode must be a number').optional().nullable(),
    cityAddress: Yup.string().optional().nullable(),
    // yup 0.32: oneOf rejects null even with .nullable() unless null is listed,
    // so a legacy org sending null here would silently block the whole save.
    phoneOwnership: Yup.string()
        .oneOf(['personal', 'company', null])
        .optional()
        .nullable(),
    phoneOperationSystems: Yup.array(
        Yup.string().oneOf(['ios', 'android'])
    ).optional().nullable(),
    passCode: Yup.string().optional().nullable(),
    alertsRetention: optionalNumber,
    reportsRetention: optionalNumber,
    mapSettingsDeviceEnabled: Yup.boolean().optional().nullable(),
    escalateCheckOnMe: Yup.boolean().default(false),
    escalateCheckOnMeTimeout: Yup.number().optional().nullable().min(1).max(10),
    safetyReportEnabled: Yup.boolean().default(true),
    checkOnMeRiskReportSurvey: Yup.boolean().default(false),
    checkOnMeRiskReportQuestion: Yup.boolean().default(false),
    checkOnMeRiskReportSurveyOverride: Yup.boolean().default(false),
    checkOnMeEmergencyContacts: Yup.boolean().default(false),
    checkOnMeDispatchEnabled: Yup.boolean().default(false),
    checkOnMeConfig: Yup.string().oneOf(["Disabled", "Enabled"]),
    checkOnMeNotifications: Yup.string().oneOf(["NONE", "EXPIRED", "EXPIRED_ESCALATED", "ESCALATED", "ALL"]),
    expireNotifyMethod: Yup.string().oneOf(["MESSAGE", "CALL", null]).optional().nullable(),
    checkoutReminderMessage: Yup.boolean().optional().nullable(),
    escalateShift: Yup.boolean().default(false),
    escalateShiftTimeout: Yup.number().optional().nullable().min(1).max(10),
    shiftDispatchEnabled: Yup.boolean().default(false),
    shiftConfig: Yup.string().oneOf(["Disabled", "Enabled"]),
    userUniqueIdentifier: Yup.string().oneOf(["INVITECODE", "EMPLOYEEID", "EMAILADDRESS"]).optional(),
    reportsButtonEnabled: Yup.boolean().optional().nullable(),
    addReportButtonEnabled: Yup.boolean().optional().nullable(),
    showAppMessageTab: Yup.boolean().optional().nullable(),
    pushNotificationPromptEnabled: Yup.boolean().optional().nullable(),
    appUpdatePromptEnabled: Yup.boolean().optional().nullable(),
    callOnDispatch: Yup.boolean().optional(),
    // Phones and emails are peer channels behind the single callOnDispatch
    // flag (node-api gates both on it). Neither list is individually
    // required — enabling the feature needs at least one recipient across
    // the two. Orgs served by an older API never receive
    // callOnDispatchEmails, so for them the rule collapses to the previous
    // phones-required behavior.
    callOnDispatchPhoneNumbers: Yup.array()
        .when('callOnDispatch', {
            is: true,
            then: (schema) => schema
                .of(Yup.string().matches(/^\+1\d{10}$/, 'Phone number must be +1 followed by 10 digits'))
                .test(
                    'at-least-one-recipient',
                    'Add at least one phone number or email address when Notify On Dispatch is enabled',
                    function atLeastOneRecipient(phones) {
                        const emails = this.parent?.callOnDispatchEmails ?? [];
                        return (phones?.length ?? 0) > 0 || emails.length > 0;
                    }
                ),
            otherwise: (schema) => schema.optional().nullable(),
        }),
    callOnDispatchEmails: Yup.array(
        Yup.string().email('Must be a valid email address')
    ).optional().nullable(),
});

// Organizations on Notification Rules edit Notify On Dispatch on the
// Notifications page, which validates it there, and the Settings page neither
// shows nor saves it, so a rule here could only block a save over a field
// nobody can see.
export const settingsFormSchemaWithoutDispatch = settingsFormSchema.omit([
    'callOnDispatch',
    'callOnDispatchPhoneNumbers',
    'callOnDispatchEmails',
]);

export const pomMasterSettingsFormSchema = Yup.object().shape({
    // Legacy orgs can have null/absent contact + address fields; every field
    // here must accept that or Formik silently refuses to submit the form
    // (matches the settingsFormSchema fix above).
    companyName: Yup.string().optional().nullable(),
    contactFirstName: Yup.string().optional().nullable(),
    contactLastName: Yup.string().optional().nullable(),
    streetAddress: Yup.string().optional().nullable(),
    stateAddress: Yup.string().optional().nullable(),
    zipcodeAddress: Yup.number().typeError('Zipcode must be a number').optional().nullable(),
    cityAddress: Yup.string().optional().nullable(),
    // yup 0.32: oneOf rejects null even with .nullable() unless null is listed,
    // so a legacy org sending null here would silently block the whole save.
    phoneOwnership: Yup.string()
        .oneOf(['personal', 'company', null])
        .optional()
        .nullable(),
    phoneOperationSystems: Yup.array(
        Yup.string().oneOf(['ios', 'android'])
    ).optional().nullable(),
    passCode: Yup.string().optional().nullable(),
    alertsRetention: optionalNumber,
    reportsRetention: optionalNumber,
    mapSettingsDeviceEnabled: Yup.boolean().optional().nullable(),
    escalateCheckOnMe: Yup.boolean().default(false),
    escalateCheckOnMeTimeout: Yup.number().optional().nullable().min(1).max(10),
    safetyReportEnabled: Yup.boolean().default(true),
    checkOnMeRiskReportSurvey: Yup.boolean().default(false),
    checkOnMeRiskReportQuestion: Yup.boolean().default(false),
    checkOnMeRiskReportSurveyOverride: Yup.boolean().default(false),
    checkOnMeEmergencyContacts: Yup.boolean().default(false),
    checkOnMeDispatchEnabled: Yup.boolean().default(false),
    checkOnMeConfig: Yup.string().oneOf(["Disabled", "Enabled"]),
    checkOnMeNotifications: Yup.string().oneOf(["NONE", "EXPIRED", "EXPIRED_ESCALATED", "ESCALATED", "ALL"]),
    expireNotifyMethod: Yup.string().oneOf(["MESSAGE", "CALL", null]).optional().nullable(),
    checkoutReminderMessage: Yup.boolean().optional().nullable(),
    escalateShift: Yup.boolean().default(false),
    escalateShiftTimeout: Yup.number().optional().nullable().min(1).max(10),
    shiftDispatchEnabled: Yup.boolean().default(false),
    shiftConfig: Yup.string().oneOf(["Disabled", "Enabled"]),
    userUniqueIdentifier: Yup.string().oneOf(["INVITECODE", "EMPLOYEEID", "EMAILADDRESS"]).optional(),
    proximityToReportsNotification: Yup.boolean().optional(),
    locationSettings: Yup.boolean().optional(),
    showInsightsDashboard: Yup.boolean().optional(),
    showRiskAssessment: Yup.boolean().optional(),
    showRiskAssessmentAddressOnly: Yup.boolean().optional(),
    riskReviewScoreThreshold: Yup.number().optional().nullable().min(1).max(5),
    isActivityReportEnabled: Yup.boolean().optional(),
    showGroupsTeamsDashboard: Yup.boolean().optional(),
    activityStatusFiltersEnabled: Yup.boolean().optional(),
    showMessageCenter: Yup.boolean().optional(),
    isSafetyReportEnabled: Yup.boolean().optional(),
    assetManagementEnabled: Yup.boolean().optional(),
    programReportEnabled: Yup.boolean().optional(),
    appointmentsCheckOnMeTrackingEnabled: Yup.boolean().optional(),
    hideLastPing: Yup.boolean().optional(),
    callOnDispatch: Yup.boolean().optional(),
    // Phones and emails are peer channels behind the single callOnDispatch
    // flag (node-api gates both on it). Neither list is individually
    // required — enabling the feature needs at least one recipient across
    // the two. Orgs served by an older API never receive
    // callOnDispatchEmails, so for them the rule collapses to the previous
    // phones-required behavior.
    callOnDispatchPhoneNumbers: Yup.array()
        .when('callOnDispatch', {
            is: true,
            then: (schema) => schema
                .of(Yup.string().matches(/^\+1\d{10}$/, 'Phone number must be +1 followed by 10 digits'))
                .test(
                    'at-least-one-recipient',
                    'Add at least one phone number or email address when Notify On Dispatch is enabled',
                    function atLeastOneRecipient(phones) {
                        const emails = this.parent?.callOnDispatchEmails ?? [];
                        return (phones?.length ?? 0) > 0 || emails.length > 0;
                    }
                ),
            otherwise: (schema) => schema.optional().nullable(),
        }),
    callOnDispatchEmails: Yup.array(
        Yup.string().email('Must be a valid email address')
    ).optional().nullable(),
    minAppVersion: Yup.string().optional().nullable(),
    ssoConfig: Yup.object().shape({
        active: Yup.boolean().default(false),
        clientId: Yup.string().when('active', {
            is: true,
            then: (schema) => schema.required('SSO Client ID is required when SSO is enabled'),
            otherwise: (schema) => schema.optional().nullable(),
        }),
        ssoUrl: Yup.string().url('Must be a valid URL').when('active', {
            is: true,
            then: (schema) => schema.required('SSO URL is required when SSO is enabled'),
            otherwise: (schema) => schema.optional().nullable(),
        }),
    }).optional().nullable(),
    reportsButtonEnabled: Yup.boolean().optional().nullable(),
    addReportButtonEnabled: Yup.boolean().optional().nullable(),
    showAppMessageTab: Yup.boolean().optional().nullable(),
    pushNotificationPromptEnabled: Yup.boolean().optional().nullable(),
    appUpdatePromptEnabled: Yup.boolean().optional().nullable(),
    // POM master only — backend strips this from non-master requests.
    pomTriggerEnabled: Yup.boolean().optional().nullable(),
});
